<template>
  <transition name="search-nav-slide-down">
    <div
      v-if="shouldShowSearch"
      ref="search"
      class="additional-nav__block site-search"
      @mouseenter="$store.dispatch('nav/setAdditionalNav', 'search')"
    >
      <div
        class="site-search__inner-container"
        :class="[
          { 'site-search__inner-container--open': showInnerContainer },
          `site-search__inner-container--${sizeVariant}-input`,
        ]"
      >
        <span
          class="icon-cross site-search__icon-cross"
          @click="$store.dispatch('nav/setAdditionalNav', '')"
        />
        <input
          type="text"
          class="site-search__input"
          :class="[`site-search__input--${sizeVariant}`]"
          :placeholder="$t('site_search.search')"
          @input="handleInputChange"
          @focus="userIsScrolling = false"
          v-model="term"
          name="search_input"
          ref="search_input"
          autocomplete="new-password"
        />
        <div class="site-search__border"></div>
        <div class="site-search__wrapper">
          <LoadingIndicator v-if="isLoading" class="site-search__loader" />
          <!-- No results -->
          <div v-if="hasNoResult" class="site-search__no-results">
            <p
              class="site-search__no-results-description"
              v-html="$t('site_search.no_results_msg', { term })"
            ></p>
            <div class="site-search__no-results-button-group">
              <button class="site-search__no-results-button" @click="followPageLink('shoe-finder')">
                {{ $t('global.go_to_shoe_finder') }}
              </button>
              <button class="site-search__no-results-button" @click="followPageLink('explore')">
                {{ $t('site_search.shop_all_products') }}
              </button>
            </div>
          </div>
          <!-- Search Intro -->
          <div v-if="introIsActive" class="site-search__intro">
            <span class="site-search__intro-header">{{ $t('site_search.recent_searches') }}:</span>
            <ul>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Cloudswift')">
                Cloudswift
              </li>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Waterproof')">
                Waterproof
              </li>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Long-T')">
                Long-T
              </li>
            </ul>
          </div>
          <!-- Search Results -->
          <div v-show="hasResult" class="site-search__products" ref="productsContainer">
            <div v-show="hasResult" class="site-search__status-bar">
              <div class="site-search__status-bar-results">
                {{ result.length }} {{ $t('site_search.results') }}
              </div>
              <a
                class="site-search__status-bar-link u-arrow-link"
                @click="followPageLink('explore')"
                >{{ $t('site_search.shop_all_products') }}</a
              >
            </div>
            <Cards
              :cards="result"
              :context="CONTEXTS.search"
              :isQuickViewEnabled="false"
              :summaryIsHidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import algoliasearch from 'algoliasearch'
import { mapGetters } from 'vuex'
import {
  notifyExternalServices,
  ACTIONS,
  CONTEXTS,
} from '@/services/external-communication/external-communication'
import { getProductsInProductImpressionFormat } from '@/helpers/format-product'
import Cards from '@/components/_shared/Cards/Cards.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'
import fetchFilterPageItems from './filter-fetch-data'

const Debouncer = On.Debouncer()
const algoliaAppId = 'ML35QLWPOC'
const algoliaKey = 'bff229776989d153121333c90db826b1'

export default {
  name: 'Search',
  components: { Cards, LoadingIndicator },

  data() {
    return {
      CONTEXTS,
      term: '',
      searchProductIndex: null,
      searchPromoCardIndex: null,
      lastSearch: '',
      result: [],
      isLoading: false,
      userIsScrolling: false,
      showInnerContainer: false,
    }
  },

  computed: {
    ...mapGetters('nav', ['shouldShowSearch']),

    introIsActive() {
      return this.term.length === 0
    },

    hasResult() {
      return this.hasSearched && this.result.length > 0
    },

    hasNoResult() {
      return this.hasSearched && this.result.length === 0
    },

    hasSearched() {
      return this.term.length > 0 && !this.isLoading
    },

    sizeVariant() {
      return this.userIsScrolling && this.hasResult ? 'small' : 'large'
    },
  },

  watch: {
    introIsActive() {
      if (!this.introIsActive) On.destroyMobileScrollHelper(this.$refs.search)
    },

    shouldShowSearch() {
      if (this.shouldShowSearch) {
        window.addEventListener('touchstart', this.blurInputField)
        window.dispatchEvent(new CustomEvent('on:search-open'))
        this.$nextTick(() => {
          this.showInnerContainer = true
          this.$refs.search_input.focus()
          On.initMobileScrollHelper(this.$refs.search)
          On.initMobileScrollHelper(this.$refs.productsContainer)
        })
      } else {
        window.removeEventListener('touchstart', this.blurInputField)
        On.destroyMobileScrollHelper(this.$refs.productsContainer)
        this.showInnerContainer = false
      }
      this.term = ''
      this.isLoading = false
      this.result = []
    },
  },

  created() {
    const client = algoliasearch(algoliaAppId, algoliaKey)
    const algoliaProductIndex = `${this.$store.state.page.environment}_products`
    const algoliaPromoCardIndex = `${this.$store.state.page.environment}_promo_cards`
    this.searchProductIndex = client.initIndex(algoliaProductIndex)
    this.searchPromoCardIndex = client.initIndex(algoliaPromoCardIndex)
  },

  methods: {
    handleInputChange() {
      this.term = this.$refs.search_input.value
      this.userIsScrolling = false
      if (this.term.length > 0 && this.lastSearch !== this.term) {
        this.result = []
        this.lastSearch = this.term
        this.isLoading = true
        Debouncer.debounce({
          callback: this.searchForItems,
          debounceTime: 400,
        })
      } else {
        this.isLoading = false
      }
    },

    async searchForItems() {
      const filterPageItems = await this.searchForFilterPageItems()
      const promoCardItems = await this.searchForPromoCardItems()
      this.result = [...filterPageItems, ...promoCardItems]
      this.isLoading = false
      this.reportToGtm(filterPageItems, this.term)
    },

    searchForFilterPageItems() {
      return this.searchProductIndex
        .search({
          query: this.term,
          restrictSearchableAttributes: [
            `translated_fields.${this.$store.state.page.language}`,
            `product_group_description_translations.${this.$store.state.page.language}`,
            'color',
            'gender',
            'product_type',
            'product_name_synonyms',
          ],
        })
        .then(data => {
          if (data.hits.length == 0) return []
          const filterPageIds = data.hits.map(d => d.filter_page_item_id)
          return fetchFilterPageItems(filterPageIds.filter(obj => obj))
        })
        .catch(() => this.resetSearch())
    },

    searchForPromoCardItems() {
      return this.searchPromoCardIndex
        .search({
          query: this.term,
          restrictSearchableAttributes: ['productGroup'],
        })
        .then(data =>
          data.hits.map(promoCard =>
            Object.assign({}, promoCard, {
              ctaText: promoCard.translated_fields[this.$store.state.page.language].cta_text,
              text: promoCard.translated_fields[this.$store.state.page.language].text,
            })
          )
        )
    },

    reportToGtm(filterPageItems, searchTerm) {
      notifyExternalServices({
        action: ACTIONS.virtPath,
        details: {
          virtPagePath: `/search?q=${searchTerm}`,
          virtPageTitle: 'Internal Search',
          searchResults: String(this.result.length),
          isNavigating: false,
        },
      })

      if (filterPageItems.length) {
        notifyExternalServices({
          action: ACTIONS.listView,
          productData: {
            items: getProductsInProductImpressionFormat(filterPageItems, CONTEXTS.search),
          },
        })
      }
    },

    insertRecommendedTerm(term) {
      this.$refs.search_input.value = term
      this.handleInputChange()
    },

    followPageLink(page) {
      this.$store.dispatch('nav/setAdditionalNav', '')
      window.location.href = `/${this.$store.state.page.locale}/${page}`
    },

    hideSearch() {
      this.$store.dispatch('nav/setAdditionalNav', '')
    },

    resetSearch() {
      this.isLoading = false
      this.result = []
    },

    blurInputField() {
      this.userIsScrolling = true
      this.$refs.search_input.blur()
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Search.scss';
</style>
